import { ArrowLeft } from 'lucide-react';
import { Outlet, useMatches, useNavigate } from '@tanstack/react-router';

import { AppSidebar } from './AppSidebar';
import { Breadcrumb } from './Breadcrumb';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { PersonalUserMenu } from './PersonalUserMenu';
import { useIsPersonalEdition } from '@/lib/auth/useEdition';
import { RecordingGuard } from '@/components/audio/RecordingGuard';
import { usePageHeader } from './PageHeaderContext';
import { FloatingRecorderPill } from '@/components/audio/FloatingRecorderPill';
import { EncounterSessionProvider } from '@/components/audio/EncounterSessionProvider';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';

/**
 * Application shell.
 *
 * The shell intentionally keeps a single, stable JSX tree across the
 * professional/personal edition split. Both editions render
 * `<SidebarProvider><SidebarInset>…<Outlet /></SidebarInset></SidebarProvider>`;
 * the sidebar is shown or hidden inside that stable tree, and edition-specific
 * header chrome is selected by props. This avoids the React reconciliation
 * pitfall where swapping the root element type (`<SidebarProvider>` ↔ `<div>`)
 * unmounts the entire route subtree, leaving TanStack Router's matched
 * `<Outlet>` content stuck in a torn state.
 *
 * `PageHeaderProvider` lives above `<RouterProvider>` in `main.tsx`. Putting
 * it there means every route — at any depth — reads the same provider via
 * plain React context, regardless of how the router stitches matches.
 */
export function AppShell() {
  const { isLoading } = useAuth();

  // Defer rendering until AuthContext settles. Otherwise the first paint
  // reads `organization === null`, picks the professional shell, then flips
  // to personal once the org arrives — the resulting branch swap fights
  // TanStack Router's match reconciliation.
  if (isLoading) return null;

  return (
    <EncounterSessionProvider>
      <ShellLayout />
      <FloatingRecorderPill />
      <RecordingGuard />
    </EncounterSessionProvider>
  );
}

function ShellLayout() {
  const isPersonal = useIsPersonalEdition();

  return (
    <SidebarProvider>
      {!isPersonal && <AppSidebar />}
      <SidebarInset>
        <AppShellHeader hideSidebarTrigger={isPersonal} />
        <div className="flex flex-1 flex-col mb-10">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

interface AppShellHeaderProps {
  hideSidebarTrigger?: boolean;
}

function AppShellHeader({ hideSidebarTrigger = false }: AppShellHeaderProps) {
  const { header } = usePageHeader();
  const navigate = useNavigate();
  const matches = useMatches();

  // The deepest route match that defines breadcrumbs in its context wins.
  // Cloning the array first so we don't mutate the value the router gave us.
  const breadcrumbs = [...matches]
    .reverse()
    .find((m) => (m.context as { breadcrumbs?: BreadcrumbItem[] } | undefined)?.breadcrumbs)?.context as
    | { breadcrumbs?: BreadcrumbItem[] }
    | undefined;
  const trail = breadcrumbs?.breadcrumbs;
  const isInnerPage = !!trail && trail.length > 1;

  const handleBack = () => navigate({ to: '/' });

  return (
    <header className="sticky top-0 z-50 overflow-hidden rounded-t-xl border-b bg-white">
      <div className="flex h-14 items-center gap-4 px-4">
        <MobileLeading isInnerPage={isInnerPage} hideSidebarTrigger={hideSidebarTrigger} onBack={handleBack} />

        {!hideSidebarTrigger && (
          <div className="hidden md:flex">
            <SidebarTrigger />
          </div>
        )}

        <DesktopTitleArea title={header?.title} hideSidebarTrigger={hideSidebarTrigger} />

        {header?.title && (
          <div className="flex min-w-0 flex-1 items-center justify-between md:hidden">
            <h1 className="truncate text-base font-semibold">{header.title}</h1>
          </div>
        )}

        {header?.actions && <div className="flex shrink-0 items-center gap-2">{header.actions}</div>}

        {hideSidebarTrigger && (
          <div className="flex shrink-0 items-center">
            <PersonalUserMenu />
          </div>
        )}
      </div>
    </header>
  );
}

interface BreadcrumbItem {
  href: string;
  title: string;
}

interface MobileLeadingProps {
  hideSidebarTrigger: boolean;
  isInnerPage: boolean;
  onBack: () => void;
}

function MobileLeading({ isInnerPage, hideSidebarTrigger, onBack }: MobileLeadingProps) {
  return (
    <div className="flex items-center gap-2 md:hidden">
      {isInnerPage ? (
        <Button size="icon" variant="ghost" className="h-9 w-9" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
          <span className="sr-only">Go back</span>
        </Button>
      ) : hideSidebarTrigger ? null : (
        <SidebarTrigger />
      )}
    </div>
  );
}

interface DesktopTitleAreaProps {
  hideSidebarTrigger: boolean;
  title?: string;
}

function DesktopTitleArea({ hideSidebarTrigger, title }: DesktopTitleAreaProps) {
  if (hideSidebarTrigger) {
    return (
      <div className="hidden min-w-0 flex-1 items-center md:flex">
        {title && <h1 className="truncate text-base font-semibold">{title}</h1>}
      </div>
    );
  }
  return (
    <div className="hidden flex-1 items-center md:flex">
      <Breadcrumb />
    </div>
  );
}
